package com.vizio.pay.api

import com.vizio.redwolf.pay.api.VizioWallet
import com.vizio.redwolf.pay.api.WalletResult
import com.vizio.redwolf.pay.model.BillingAddress
import com.vizio.redwolf.pay.model.NewCreditCardInfo
import com.vizio.redwolf.pay.model.UpdateCreditCardInfo
import com.vizio.redwolf.utils.AppConfig
import com.vizio.redwolf.utils.PayEnvironment
import com.vizio.redwolf.utils.ResponseHeaders
import com.vizio.redwolf.utils.clientprovider.WebApiUseCase
import kotlin.js.Promise

@JsExport
class PayJsApi(environment: PayEnvironment, jsClientConfig: JsClientConfig? = null) {
    private val wallet = VizioWallet(payEnv = environment,
                                     appConfig = AppConfig(""),
                                     clientConfig = jsClientConfig.toClientConfig(WebApiUseCase.COOKIE_SDK_HEADERS)
    )

    @JsName("getCreditCards")
    fun getCreditCards(token: String, traceId: String? = null): Promise<dynamic> {
        return handleJsPromiseRequest(exceptionHandler = ::handleWalletJsException) {
            val response = wallet.getCreditCards(token, traceId = traceId)
            convertListToJsWrapper(response)
        }
    }

    @JsName("addCreditCard")
    fun addCreditCard(token: String, jsNewCardInfo: dynamic, traceId: String? = null): Promise<dynamic> {
        return handleJsPromiseRequest(exceptionHandler = ::handleWalletJsException) {
            val reCaptchaToken = jsNewCardInfo.reCaptchaToken as String?
            val newCardInfo = NewCreditCardInfo(
                cardNonce = jsNewCardInfo.cardNonce as String,
                isDefault = jsNewCardInfo.isDefault as Boolean,
                billingAddress = BillingAddress(
                    postalCode = jsNewCardInfo.billingAddress.postalCode as String,
                    firstName = jsNewCardInfo.billingAddress.firstName as String?,
                    lastName = jsNewCardInfo.billingAddress.lastName as String?,
                    streetAddress = jsNewCardInfo.billingAddress.streetAddress as String?,
                    additionalAddress = jsNewCardInfo.billingAddress.additionalAddress as String?,
                    city = jsNewCardInfo.billingAddress.city as String?,
                    state = jsNewCardInfo.billingAddress.state as String?,
                ),
            )
            val response = wallet.addCreditCard(token, newCardInfo, reCaptchaToken, traceId = traceId)
            convertListToJsWrapper(response)
        }
    }

    @JsName("deleteCreditCard")
    fun deleteCreditCard(token: String, cardToken: String, traceId: String? = null): Promise<dynamic> {
        return handleJsPromiseRequest(exceptionHandler = ::handleWalletJsException) {
            val response = wallet.deleteCreditCard(token, cardToken, traceId = traceId)
            convertListToJsWrapper(response)
        }
    }

    @JsName("getDefaultCredit")
    fun getDefaultCredit(token: String, traceId: String? = null): Promise<dynamic> {
        return handleJsPromiseRequest(exceptionHandler = ::handleWalletJsException) {
            val response = wallet.getDefaultCreditCard(token, traceId = traceId)
            convertListToJsWrapper(response)
        }
    }

    @JsName("setDefaultCreditCard")
    fun setDefaultCreditCard(token: String, cardToken: String, traceId: String? = null): Promise<dynamic> {
        return handleJsPromiseRequest(exceptionHandler = ::handleWalletJsException) {
            val response = wallet.setDefaultCreditCard(token, cardToken, traceId = traceId)
            convertListToJsWrapper(response)
        }
    }

    @JsName("updateCreditCard")
    fun updateCreditCard(token: String, cardToken: String, jsUpdateCreditCardInfo: dynamic, traceId: String? = null): Promise<dynamic> {
        return handleJsPromiseRequest(exceptionHandler = ::handleWalletJsException) {
            val updateCreditCardInfo = UpdateCreditCardInfo(
                cvv = jsUpdateCreditCardInfo.cvv as String,
                expirationMonth = jsUpdateCreditCardInfo.expirationMonth as Int,
                expirationYear = jsUpdateCreditCardInfo.expirationYear as Int,
                billingAddress =
                if (jsUpdateCreditCardInfo.billingAddress != null)
                    BillingAddress(
                        postalCode = jsUpdateCreditCardInfo.billingAddress.postalCode as String,
                        firstName = jsUpdateCreditCardInfo.billingAddress.firstName as String?,
                        lastName = jsUpdateCreditCardInfo.billingAddress.lastName as String?,
                        streetAddress = jsUpdateCreditCardInfo.billingAddress.streetAddress as String?,
                        additionalAddress = jsUpdateCreditCardInfo.billingAddress.additionalAddress as String?,
                        city = jsUpdateCreditCardInfo.billingAddress.city as String?,
                        state = jsUpdateCreditCardInfo.billingAddress.state as String?,
                    )
                else null
            )
            val response = wallet.updateCreditCard(token, cardToken, updateCreditCardInfo, traceId = traceId)
            convertListToJsWrapper(response)
        }
    }

    @JsName("getBraintreeClientToken")
    fun getBraintreeClientToken(traceId: String? = null): Promise<dynamic> {
        return handleJsPromiseRequest(exceptionHandler = ::handleWalletJsException) {
            val response = wallet.getBraintreeClientToken(traceId = traceId)
            convertListToJsWrapper(response)
        }
    }

    private fun <T : Any> handleWalletJsException(ex: Exception, data: JsExceptionInfo<T>? = null): WalletResult.Error {
        return WalletResult.Error(ResponseHeaders.Invalid, Exception(jsRequestExceptionError))
    }

    private fun <T : Any> convertListToJsWrapper(response: WalletResult<T>): WalletResult<Any> {
        return (response as? WalletResult.Success)?.let { success ->
            (success.data as? List<Any?>)?.let { list ->
                val jsArrayWrapper = JsArrayWrapper(list.toTypedArray())
                WalletResult.Success(success.headers, jsArrayWrapper)
            }
        } ?: response
    }
}